import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getAllPurchaseTickets } from "../../redux/slices/purchaseTicketsSlice";
import { useTranslation } from "react-i18next";
import BackdropSpinner from "../../components/backdropSpinner";

const PurchasedTickets = () => {
  const dispatch = useDispatch();
  const { allPurchaseTickets, loading } = useSelector(
    (state) => state.purchase
  );
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState("thisMonth");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [errors, setError] = useState("");
  const userId = localStorage.getItem("userID");

  useEffect(() => {
    if (userId) {
      const payload = { userId, dateRange };
      dispatch(getAllPurchaseTickets(payload));
    }
  }, [dispatch, userId]);

  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0"); // Ensure 2 digits
    const day = String(d.getDate()).padStart(2, "0"); // Ensure 2 digits
    return `${year}-${month}-${day}`;
  };

  const handleFilterTickets = () => {
    const today = new Date();
    const start_date = new Date(fromDate);
    const end_date = new Date(toDate);

    // Validate if dates are in the future
    if (fromDate && start_date > today) {
      setError(t("ticketDetails.errors.fromDateFuture"));
      return;
    }
    if (toDate && end_date > today) {
      setError(t("ticketDetails.errors.toDateFuture"));
      return;
    }

    // Validate if "To" date is before "From" date
    if (fromDate && toDate && start_date > end_date) {
      setError(t("ticketDetails.errors.toDateBeforeFromDate"));
      return;
    }

    // Clear error and proceed
    setError("");
    if (fromDate && toDate) {
      dispatch(
        getAllPurchaseTickets({
          userId: userId,
          start_date: formatDate(fromDate),
          end_date: formatDate(toDate),
        })
      );
    }
  };

  const handleClearFilters = () => {
    setFromDate("");
    setToDate("");
    setError("");
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    if (range !== "custom") {
      // Automatically dispatch when 'thisMonth' or 'last3Months' is selected
      const payload = { userId, dateRange: range };
      dispatch(getAllPurchaseTickets(payload));
      handleClearFilters();
    }
  };

  const sortedTickets =
    allPurchaseTickets?.tickets?.length > 0
      ? [...allPurchaseTickets.tickets].sort(
          (a, b) => new Date(b.purchase_date) - new Date(a.purchase_date)
        )
      : [];

  if (loading) {
    return <BackdropSpinner />;
  }

  return (
    <>
      <div className="flex flex-col items-center my-5 space-y-3">
        {/* Title */}
        <div className="flex justify-center my-2">
          <p className="text-white text-lg sm:text-xl md:text-2xl font-medium text-center">
            {t("purchasedTickets.tableTitle")}
          </p>
        </div>
        {/* Date Range Buttons */}
        <div className="flex flex-wrap justify-center gap-3 w-full px-4">
          {["thisMonth", "last3Months", "custom"].map((range) => (
            <button
              key={range}
              className={`p-2 px-3 sm:px-5 rounded-lg font-semibold text-sm sm:text-base transition-all duration-300 ${
                dateRange === range
                  ? "bg-gradient-to-r from-[#C38B20] to-[#E5AA36] text-black shadow-lg"
                  : "bg-gray-800 text-white hover:bg-gray-700"
              }`}
              onClick={() => handleDateRangeChange(range)}
            >
              {t(`paymentTransactions.${range}`)}
            </button>
          ))}
        </div>
      </div>

      {dateRange === "custom" && (
        <div>
          <div className="flex flex-row justify-center items-center gap-2 lg:gap-4 m-4 lg:mx-20">
            {/* Start Date */}
            <div className="w-full sm:w-1/2 md:w-full lg:w-full">
              <label
                htmlFor="start"
                className="block text-xs sm:text-sm font-medium text-[#FFBF4D] mb-1"
              >
                {t("paymentTransactions.startDate")}
              </label>
              <input
                type="date"
                className="w-full lg:w-full md:w-full sm:w-1/2 p-2 rounded border border-[#E2A32C] text-black"
                placeholder="From"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            {/* End Date */}
            <div className="w-full sm:w-1/2 md:w-full lg:w-full">
              <label
                htmlFor="end"
                className="block text-xs sm:text-sm font-medium text-[#FFBF4D] mb-1"
              >
                {t("paymentTransactions.endDate")}
              </label>
              <input
                type="date"
                className="w-full lg:w-full md:w-full sm:w-1/2 p-2 rounded border border-[#E2A32C] text-black"
                placeholder="To"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>

          {/* Display validation error */}
          {errors && <div className="text-red-500 mb-4">{errors}</div>}

          {/* Apply Filter Button */}
          <div className="w-full flex justify-center mb-3">
            <button
              onClick={handleFilterTickets}
              className="bg-gradient-to-r from-[#C38B20] to-[#E5AA36] text-black font-semibold p-2 px-5 rounded-lg shadow-lg hover:bg-gradient-to-l transition-all duration-300"
            >
              {t("paymentTransactions.applyFilter")}
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto p-4">
        <div className="overflow-x-auto shadow-lg border border-[#E2A32C] rounded-lg">
          <table className="w-full rounded-lg">
            <thead className="bg-gradient-to-r from-[#C38B20] via-[#E5AA36] to-[#FFBF4D] text-black font-bold uppercase">
              <tr>
                <th className="text-left py-4 px-4 font-semibold text-xs sm:text-sm">
                  {t("purchasedTickets.ticketNumber")}
                </th>
                <th className="text-left py-4 px-4 font-semibold text-xs sm:text-sm">
                  {t("purchasedTickets.ticketQuantity")}
                </th>
                <th className="text-left py-4 px-4 font-semibold text-xs sm:text-sm">
                  {t("purchasedTickets.totalPrice")}
                </th>
                <th className="text-left py-4 px-4 font-semibold text-xs sm:text-sm">
                  {t("purchasedTickets.purchaseDate")}
                </th>
              </tr>
            </thead>
            <tbody className="text-gray-100">
              {sortedTickets.length > 0 ? (
                sortedTickets.map((ticket) => (
                  <tr key={ticket.id} className="border-b border-[#E2A32C]">
                    <td className="py-4 px-4 text-xs sm:text-sm">
                      {ticket.ticket_number}
                    </td>
                    <td className="py-4 px-4 text-xs sm:text-sm">
                      {ticket.ticket_quantity}
                    </td>
                    <td className="py-4 px-4 text-xs sm:text-sm">
                      ₹{ticket.total_price}
                    </td>
                    <td className="py-4 px-4 text-xs sm:text-sm">
                      {new Date(ticket.purchase_date).toLocaleDateString(
                        "en-GB",
                        {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        }
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    className="text-center py-8 text-gray-600 text-lg"
                  >
                    {t("purchasedTickets.noTicketsMessage")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default PurchasedTickets;
